import React, { ReactNode, useContext, useEffect, useState } from 'react';
import {
  Button, Grid, TextField, Typography, FormControl, InputAdornment, MenuItem,
  Select, SelectChangeEvent, TableCell, TableRow, TableContainer, Table, TableHead, TableBody,
  TableSortLabel,
  SvgIcon, useMediaQuery, useTheme, InputLabel,
} from '@mui/material';
import useNswagClient from '../../../hooks/api/useNswagClient';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

import { getFormattedDate } from '../../../utils';
import { useTranslation } from 'react-i18next';
import CustomPagination from '../../../components/forms/Pagination';
import { useNavigate } from 'react-router-dom';
import { StockCheckStatus, StockCheck } from '../../../app/services/api/generated';
import { UserContext } from '../../../components/shared/useUser';
import { StockCheckStatus as StockCheckStatusEnum } from '../../../enums/StockCheckStatus';
import { ActiveTab } from '../../../enums/ActiveTab';
import { Trash01, SearchSm } from '../../../assets';
import { Section } from '../../../components/ui/Section';
import { TableHeader } from '../../../components/forms/TableHeader';
import { Pill } from '../../../components/shared/Pill';
import { EmptyRows } from '../../../components/forms/EmptyRows';
import { TableLoading } from '../../../components/forms/TableLoading';
import { ConfirmationDeleteDialog } from '../../../components/shared/dialogs/ConfirmationDeleteDialog';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import { StockCheckType, StockCheckTypeLabels } from '../../../enums/StockCheckType';

interface Status {
  index: number | string,
  description: string,
  active?: boolean | null,
}
const allStatuses: Status[] = [
  { index: 0, description: 'All' },
  { index: 3, description: 'Pending', active: true },
  { index: 1, description: 'Counting', active: true },
  { index: 4, description: 'Pending Approval', active: true },
  { index: 2, description: 'Completed', active: false },
];

interface TabState {
  activeTab: ActiveTab;
  handleClick: () => void;
}

const StockCountPage: React.FC<TabState> = ({ activeTab, handleClick }) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const [page, setPage] = React.useState(0);
  const rowsPerPage = 10;
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState<StockCheck[]>([]);
  const [count, setCount] = useState<number | undefined>();
  const [selectedDate, setSelectedDate] = useState<string>();
  const [selectedStatus, setSelectedStatus] = useState<StockCheckStatus>(0);
  const { getStockCheck } = useNswagClient();
  const { deleteStockCheck } = useNswagClient();
  const navigate = useNavigate();
  const [currentRowId, setCurrentRowId] = useState<number>(0);
  const [showAlert, setShowAlert] = useState(false);
  const [open, setOpen] = useState(false);
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState<boolean>(true);
  const [currentTab, setCurrentTab] = useState<boolean>(false);
  const [reset, setReset] = useState<boolean>(false);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string | Date | number>();
  const [selectedType, setSelectedType] = useState<number>(0); 

  useEffect(() => {
    getData();
  }, [page, currentTab, reset]);

  useEffect(() => {
    setLoading(true);
    if (activeTab === ActiveTab.Inactive) {
      setSelectedStatus(2);
      setCurrentTab(true);
    }
    if (activeTab === ActiveTab.Active) {
      setSelectedStatus(0);
      setCurrentTab(false);
    }
    setPage(0);
  }, [activeTab]);

  const getData = () => {
    setLoading(true);
    const storeNumber = selectedStore?.storeNumber;
    const skip = rowsPerPage * page;
    const status = selectedStatus !== 0 ? selectedStatus : undefined;
    getStockCheck(storeNumber, searchTerm, selectedDate, status, skip, rowsPerPage, selectedType).then((result) => {
      if (result) {
        const finaldata = result.data?.data;
        if (finaldata) {
          setData(finaldata);
        }
        setCount(result.data?.totalCount);
      } else {
        console.error('Invalid API response:', result);
      }
    })
      .catch((error) => {
        console.error('Error fetching data:', error);
      })
      .finally(() => { setLoading(false); });
  };

  const searchStock = () => {
    if (page === 0) {
      getData();
    } else {
      setPage(0);
    }
  };

  const onDeleteConfirm = async (id: number) => {
    await deleteStockCheck(id);
    setOpen(false);
    getData();
    setShowAlert(true);
  };

  const getStatus = (status: StockCheckStatus | undefined): string => {
    if (status === undefined) {
      return '';
    }
    const filteredStatus = allStatuses.filter(e => e.index == status);
    return filteredStatus.length === 0 ? '' : filteredStatus[0].description;
  };

  const comparator = (property: keyof StockCheck) => (a: StockCheck, b: StockCheck): number => {
    const isAsc = orderBy === property && order === 'asc';
    if (typeof a[property] === 'string' || a[property] instanceof Date) {
      return isAsc
        ? String(a[property]).localeCompare(String(b[property]))
        : String(b[property]).localeCompare(String(a[property]));
    } else {
      return isAsc ? Number(a[property]) - Number(b[property]) : Number(b[property]) - Number(a[property]);
    }
  };

  const toSort = (arr: StockCheck[], compareFn: (a: StockCheck, b: StockCheck) => number): StockCheck[] => {
    return arr.slice().sort(compareFn);
  };

  const handleSort = (property: keyof StockCheck) => {
    const isAsc = orderBy === property && order === 'asc';
    if (data) {
      const sortedData = toSort(data, comparator(property));
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
      setData(sortedData);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusChange = (event: SelectChangeEvent<StockCheckStatus>, child: ReactNode) => {
    setSelectedStatus(event.target.value as StockCheckStatus);
  };

  const handleDateChange = (date: string | null) => {
    setSelectedDate(date ?? '');
  };
  const handleTypeChange = (event: SelectChangeEvent<StockCheckType | ''>, child: ReactNode) => {
    setSelectedType(event.target.value as StockCheckType);
  };

  const clearAll = () => {
    setPage(0);
    setSearchTerm('');
    setSelectedDate('');
    setSelectedStatus(activeTab === ActiveTab.Inactive ? StockCheckStatusEnum.Completed : 0);
    setReset(!reset);
    setSelectedType(0);
  };

  const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>, rowId: number, status: number) => {
    const id = rowId.toString();

    if (status === StockCheckStatusEnum.PendingApproval || status === StockCheckStatusEnum.Completed) {
      navigate(`/store/review-stock-count/${id}`);
    }
    if (status === StockCheckStatusEnum.Pending || status === StockCheckStatusEnum.Counting) {
      navigate(`/store/perform-stock-count/${id}`);
    }
  };

  const handleOnDelete = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, rowId: number) => {
    event.stopPropagation();

    setCurrentRowId(rowId);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const maxPages = () => count ? Math.ceil(count / rowsPerPage) : 1;

  return (
    <>
      <Grid
        container
        spacing={5}>
        <Grid item
          lg={4}
          xs={12}>
          <TextField
            fullWidth
            label={t('stockcounts.search')}
            // placeholder={t('searchByItemNumberOrDescription')}
            id="input-with-icon-textfield"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SvgIcon><SearchSm /></SvgIcon>
                </InputAdornment>
              ),
            }}
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </Grid>
        <Grid item
          lg={4}
          xs={6}>
          <DatePickerLocalized label={t('stockcounts.date')}
            useCalendar={false}
            value={selectedDate}
            onChange={handleDateChange} />
        </Grid>
        <Grid item
          lg={4}
          xs={6}>
          <FormControl fullWidth
            size="small">
            <InputLabel>{t('stockcounts.type')}</InputLabel>
            <Select
              value={selectedType}
              onChange={handleTypeChange}
              label={t('stockcounts.type')}
            >
              {Object.values(StockCheckType)
                .filter(value => typeof value === 'number')
                .map((type) => (
                  <MenuItem key={type}
                    value={type}>
                    {StockCheckTypeLabels[type as StockCheckType]}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
        {activeTab === ActiveTab.Active && (
          <Grid
            item
            lg={4}
            xs={6}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}
          >
            <FormControl fullWidth
              size="small">
              <InputLabel id="type-label">{t('stockcounts.status')}</InputLabel>
              <Select
                id="order-type"
                value={selectedStatus}
                onChange={handleStatusChange}
                label={t('stockcounts.status')}
              >
                {allStatuses.filter(e => e.index !== 2).map((status) => (
                  <MenuItem key={status.index}
                    value={status.index}>
                    {status.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item
          lg={2}
          xs={6}>
          <Button fullWidth
            variant="secondary"
            size="lg"
            onClick={clearAll}
          >
            Clear All
          </Button>
        </Grid>
        <Grid item
          lg={2}
          xs={6}>
          <Button fullWidth
            variant="primary"
            size="lg"
            onClick={searchStock}
            startIcon={<SearchSm />}
          >
            {t('search')}
          </Button>
        </Grid>
        <Grid item
          xs={12}>
          <>
            <Section>
              {(data.length === 0 && !loading) ? <EmptyTable text={t('stockcounts.title')}
                onClear={clearAll}
                onAdd={handleClick}
                addItemText={t('stockcounts.newitem')}
                onAddIsCreating={false}
                headerHeight={45}
                bodyRowHeight={40} />
                :
                <>
                  <TableHeader>
                    {t('stockcounts.title')}
                    {(count ?? 0) > 0 && <Pill text={`${count} ${t('stockcounts.title')}`}></Pill>}
                  </TableHeader>
                  {loading ? <TableLoading headerHeight={45}
                    bodyRowHeight={40}
                    minTableWidth={900} /> :
                    <TableContainer component={'div'}>
                      <Table sx={{ minWidth: '900px' }}
                        aria-label="a table">
                        <TableHead>
                          <TableRow >
                            <TableCell>
                              Id
                            </TableCell>
                            <TableCell>
                              {t('stockcounts.author')}
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={orderBy === 'name'}
                                direction={order}
                                onClick={() => handleSort('name')}
                              >
                                {t('stockcounts.name')}
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={orderBy === 'status'}
                                direction={order}
                                onClick={() => handleSort('status')}
                              >
                                {t('stockcounts.status')}
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={orderBy === 'plannedCountDate'}
                                direction={order}
                                onClick={() => handleSort('plannedCountDate')}
                              >
                                {t('stockcounts.dates')}
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={orderBy === 'stockCheckType'}
                                direction={order}
                                onClick={() => handleSort('stockCheckType')}
                              >
                                {t('stockcounts.type')}
                              </TableSortLabel>
                            </TableCell>
                            {
                              hasPermissionTo(['StockCountDelete']) &&
                              <TableCell>
                              </TableCell>
                            }
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data?.map((row: StockCheck) => (
                            <TableRow key={row.id}
                              sx={{ cursor: 'pointer' }}
                              onClick={(event) => handleRowClick(event, row?.id ?? 0, row?.status ?? 0)}>
                              <TableCell>
                                <Typography variant='textXS'
                                  fontWeight={600}
                                  color={theme.palette.primary[500]}
                                  sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                  {row.id}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 0 }}>
                                <AccountCircleIcon titleAccess={row.createdBy ?? ''} />
                              </TableCell>
                              <TableCell>
                                {row.name}
                              </TableCell>
                              <TableCell>
                                {getStatus(row.status)}
                              </TableCell>
                              <TableCell>
                                {(row.plannedCountDate === undefined ? '' : getFormattedDate(new Date(row.plannedCountDate)))}
                              </TableCell>
                              <TableCell>
                                {row.stockCheckType != null && StockCheckType[row.stockCheckType as StockCheckType]
                                  ? StockCheckTypeLabels[row.stockCheckType as StockCheckType]
                                  : t('noType')}
                              </TableCell>
                              {
                                hasPermissionTo(['StockCountDelete']) &&
                                <TableCell sx={{ py: 0 }}>
                                  <Trash01
                                    color={theme.palette.error[500]}
                                    onClick={($event) => handleOnDelete($event, row.id ?? 0)} />
                                </TableCell>
                              }
                            </TableRow>
                          ))}
                          <EmptyRows numberOfCurrentRows={data.length}
                            height={40} />
                        </TableBody>
                      </Table>
                    </TableContainer>}
                  <CustomPagination
                    page={page}
                    setPage={setPage}
                    maxPages={maxPages()}
                    breakpointForChangeDisplay={120000} />
                </>}
            </Section>
          </>
        </Grid>
      </Grid >
      <ConfirmationDeleteDialog open={open}
        onConfirm={() => onDeleteConfirm(currentRowId)}
        onCancel={handleClose}
        nameOfObject={`Stock count: ${currentRowId}`} />
      <MessagesSnackbar open={showAlert}
        onClose={() => { setShowAlert(false); }}
        message={'Stock count deleted successfully.'}
        severity={'success'}
        duration={15000} />
    </>
  );
};
export default StockCountPage;
